import React, { useEffect, useState } from 'react';
import {
    Modal, Form, Input, InputNumber, Select, Button, Space, Alert,
    Typography, Divider, message, Checkbox,
} from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import TelnaApiService from '../../APIServices/TelnaApiService';
import { formatBytes, formatValidity } from './telnaHelpers';

const { Text } = Typography;

/**
 * Create a Telna order.
 *
 * Two things this form has to make honest, because neither is obvious:
 *
 * 1. **Price is typed in, not looked up.** Telna's API carries no cost at all —
 *    a package template has an allowance and a country list and nothing else,
 *    and what we pay surfaces only as wallet drawdown against a contracted rate
 *    card. Unlike the eSIM Access flow, nothing can compute this. Left empty,
 *    the order is stored as "unpriced" rather than as zero, because a free
 *    replacement and a price nobody has set yet are different facts.
 *
 * 2. **"Provision now" spends money.** It is off by default and labelled as
 *    such. With the server's spend switch off it is refused outright, and the
 *    order is still created as a draft that can be provisioned later.
 */
const CreateTelnaOrderModal = ({ open, onClose, onCreated, provisioningEnabled, availableStock }) => {
    const [form] = Form.useForm();
    const [templates, setTemplates] = useState([]);
    const [loadingTemplates, setLoadingTemplates] = useState(false);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open) return;
        setLoadingTemplates(true);
        TelnaApiService.getPackageTemplates()
            .then((res) => setTemplates(res.templates || []))
            .catch((err) => message.error(err.message))
            .finally(() => setLoadingTemplates(false));
    }, [open]);

    const submit = async (values) => {
        setSaving(true);
        try {
            const payload = {
                customer_email: values.customer_email,
                customer_name: values.customer_name || '',
                // Empty stays null: "unpriced" is not the same as free.
                price: values.price === undefined || values.price === null ? null : values.price,
                currency: values.currency || 'EUR',
                source: values.source || 'admin',
                payment_reference: values.payment_reference || '',
                notes: values.notes || '',
                items: (values.items || []).map((i) => ({ template_id: i.template_id, quantity: i.quantity || 1 })),
                provision: Boolean(values.provision),
            };
            const res = await TelnaApiService.createOrder(payload);

            if (res.provisioning?.error) {
                // The order exists; only fulfilment failed. Say both, or the
                // operator creates a duplicate believing nothing was saved.
                message.warning(`Order ${res.order.order_number} created, but provisioning failed: ${res.provisioning.message}`);
            } else if (res.provisioning) {
                message.success(`Order ${res.order.order_number} created — ${res.provisioning.completed}/${res.provisioning.attempted} eSIMs provisioned`);
            } else {
                message.success(`Order ${res.order.order_number} created as a draft`);
            }

            form.resetFields();
            onCreated?.(res.order);
            onClose();
        } catch (err) {
            message.error(err.message);
        } finally {
            setSaving(false);
        }
    };

    const templateOptions = templates.map((t) => ({
        value: t.id,
        label: `${t.name} — ${formatBytes(t.dataAllowanceBytes)} / ${formatValidity(t)} / ${t.supportedCountriesIso3?.length || 0} countries`,
    }));

    return (
        <Modal
            title="New Telna eSIM order"
            open={open}
            onCancel={onClose}
            width={720}
            footer={null}
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={submit}
                initialValues={{ items: [{ quantity: 1 }], currency: 'EUR', source: 'admin', provision: false }}
            >
                {!provisioningEnabled && (
                    <Alert
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                        message="Provisioning is switched off"
                        description="The order will be saved as a draft. Turn on TELNA_PROVISIONING_ENABLED on the server to buy eSIMs."
                    />
                )}
                {typeof availableStock === 'number' && availableStock <= 3 && (
                    <Alert
                        type={availableStock === 0 ? 'error' : 'warning'}
                        showIcon
                        style={{ marginBottom: 16 }}
                        message={availableStock === 0 ? 'No sellable eSIMs left' : `Only ${availableStock} sellable eSIM${availableStock === 1 ? '' : 's'} left`}
                        description="Telna inventory is pre-loaded — there is no reorder API. Ask Telna to load more before this runs out."
                    />
                )}

                <Space size="large" style={{ display: 'flex' }}>
                    <Form.Item
                        name="customer_email"
                        label="Customer email"
                        rules={[{ required: true, message: 'Required' }, { type: 'email', message: 'Not a valid email' }]}
                        style={{ flex: 1 }}
                    >
                        <Input placeholder="traveller@example.com" />
                    </Form.Item>
                    <Form.Item name="customer_name" label="Customer name" style={{ flex: 1 }}>
                        <Input placeholder="Optional" />
                    </Form.Item>
                </Space>

                <Divider orientation="left" plain>Plans</Divider>

                <Form.List name="items">
                    {(fields, { add, remove }) => (
                        <>
                            {fields.map((field) => (
                                <Space key={field.key} align="baseline" style={{ display: 'flex', marginBottom: 8 }}>
                                    <Form.Item
                                        {...field}
                                        name={[field.name, 'template_id']}
                                        rules={[{ required: true, message: 'Pick a plan' }]}
                                        style={{ flex: 1, minWidth: 380, marginBottom: 0 }}
                                    >
                                        <Select
                                            placeholder="Package template"
                                            loading={loadingTemplates}
                                            options={templateOptions}
                                            style={{ width: 420 }}
                                            notFoundContent={loadingTemplates ? 'Loading…' : 'Telna has published no templates on this inventory'}
                                        />
                                    </Form.Item>
                                    <Form.Item {...field} name={[field.name, 'quantity']} style={{ marginBottom: 0 }}>
                                        <InputNumber min={1} max={20} placeholder="Qty" style={{ width: 80 }} />
                                    </Form.Item>
                                    {fields.length > 1 && (
                                        <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(field.name)} />
                                    )}
                                </Space>
                            ))}
                            <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ quantity: 1 })} block>
                                Add another plan
                            </Button>
                        </>
                    )}
                </Form.List>

                <Divider orientation="left" plain>Commercial</Divider>

                <Space size="large" style={{ display: 'flex' }}>
                    <Form.Item
                        name="price"
                        label="Price charged"
                        style={{ flex: 1 }}
                        extra={<Text type="secondary" style={{ fontSize: 12 }}>Telna publishes no price — leave empty for unpriced.</Text>}
                    >
                        <InputNumber min={0} step={0.5} style={{ width: '100%' }} placeholder="e.g. 12.50" />
                    </Form.Item>
                    <Form.Item name="currency" label="Currency" style={{ width: 110 }}>
                        <Select options={[{ value: 'EUR' }, { value: 'USD' }, { value: 'GBP' }]} />
                    </Form.Item>
                    <Form.Item name="source" label="Source" style={{ width: 140 }}>
                        <Select options={[{ value: 'admin' }, { value: 'website' }, { value: 'mobile' }, { value: 'amazon' }, { value: 'api' }]} />
                    </Form.Item>
                </Space>

                <Form.Item name="payment_reference" label="Payment reference">
                    <Input placeholder="Stripe / Mollie id, or how it was paid for" />
                </Form.Item>

                <Form.Item name="notes" label="Notes">
                    <Input.TextArea rows={2} placeholder="Why this order exists — replacement, test, partner deal…" />
                </Form.Item>

                <Form.Item name="provision" valuePropName="checked">
                    <Checkbox disabled={!provisioningEnabled}>
                        Provision immediately&nbsp;
                        <Text type="danger" style={{ fontSize: 12 }}>(buys the eSIMs — spends wallet balance)</Text>
                    </Checkbox>
                </Form.Item>

                <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button onClick={onClose}>Cancel</Button>
                    <Button type="primary" htmlType="submit" loading={saving}>Create order</Button>
                </Space>
            </Form>
        </Modal>
    );
};

export default CreateTelnaOrderModal;
